import React, { Component } from "react";
import { scaleSqrt } from "d3-scale";
import { extent } from "d3-array";
import moment from "moment";
import { getCoords, radialArea, SQRT2, colors } from '../../utils';

import {
  PositiveFace,
  NegativeFace,
  NeutralFace,
  NeutralFaceDef,
  PositiveFaceDef,
  NegativeFaceDef
} from "./emoticons.js";

import Standfirst from '../Layout/Standfirst';
import Methodology from '../Layout/Methodology';
import Who from '../Layout/Who';
import Footer from '../Layout/Footer';
import Keys from '../Layout/Keys';
import ArticleTexts from '../Layout/ArticleTexts';
import VisualTimeline from '../Layout/VisualTimeline';
import "./bubbles.css";

const popularityScale = scaleSqrt();

const MIN_ARTICLES_FOR_TICKS = 5;

const MAX_SIDE = 300;

const sides = 4;
const SIDE = 30;
const SQUARE_MARGIN = 6;

const daysToElections = Math.ceil( Math.abs( new Date("3/4/2018") - new Date()) / (1000 * 3600 * 24) );
const visualTimelineTexts = [];

class Bubbles extends Component {
  constructor(props) {
    super(props);
    this.squareMap = [];
    this.currentScrollTop = 0;
    this.currentSquare = null;
    this.state = {
      cells: [],
      articles: [],
      ticks: {},
      height: 0,
      visualTimelineTexts: [],
    };
  }
  componentDidMount() {
    document.querySelector('.elezioni-parallax').addEventListener('scroll', (e) => {this._handleScroll(e)});

    const update = (timestamp) => {
        let found = null;
        if(this.currentScrollTop > this.state.height) {
          this.deHighlightSquare(this.currentSquare);
          this.ArticleTexts.hideText(this.currentSquare);
          this.currentSquare = null;
          window.requestAnimationFrame(update);
          return;
        }
        if(this.currentScrollTop < this.state.height) {
          for(let i=0;i<this.squareMap.length;i++) {
            if(this.squareMap[i][1] <= this.currentScrollTop) {
              found = this.squareMap[i][0];
            } else {
              break;
            }
          }

          if(!found) {
            this.deHighlightSquare(this.currentSquare);
            this.ArticleTexts.hideText(this.currentSquare);
            this.currentSquare = null;
          } else {
            if(found !== this.currentSquare) {
              this.currentSquare = found;
              this.highlightSquare(this.currentSquare);
              this.ArticleTexts.showText(this.currentSquare);
            }
          }
        }
        window.requestAnimationFrame(update);
    }
    update();
  }
  componentWillReceiveProps(nextProps) {
    const { articles, width, hours, infoBlocks } = nextProps;

    const popExtent = extent(articles, d => +d.popularity);
    const maxSide = width/2 > MAX_SIDE ? MAX_SIDE : width/2;
    popularityScale.domain(popExtent).range([1000, maxSide * maxSide]);

    const ticks = {};
    const visualTimelineTexts = [];
    let prev = null;
    const squares = articles.map((d, i) => {
      const size = popularityScale(d.popularity);
      let side = Math.sqrt(size);
      let diag = SQRT2 * side;
      diag = Math.floor(diag / SIDE) * SIDE;
      side = diag / SQRT2;
      const points = getCoords(side, sides, SQUARE_MARGIN);
      const square = {
        id: i,
        side,
        size,
        diag: SQRT2 * side,
        points,
        sibling: prev,
        party: d.party,
        collections: d.collections,
        slugs: d.slugs,
        article: d
      };
      prev = square;
      return square;
    });

    let height = 0;
    let availableSide = 0;
    const cells = squares.map(d => {
      if (!d.sibling) {
        d.position = [0, d.diag];
        availableSide = d.side;
        d.direction = 1;
        d.parent = d;
        d.position = [0, d.diag];

        ticks[d.article.hour] = {
          hour: moment(d.article.hour).format("HH:00"),
          date: moment(d.article.hour).format("DD MMM"),
          y: d.position[1],
        };
        //console.log(d.position[1], d.article.id)
        //this.squareMap[d.position[1]] = d.article.id;
        this.squareMap.push([d.article.id,d.position[1]])

        return d;
      }
      availableSide = availableSide - d.side;
      if (d.sibling.parent.id === d.sibling.id) {
        d.parent = d.sibling.parent;
        d.direction = d.sibling.direction;
      } else {
        d.parent = d.sibling.parent;
        d.direction = -d.parent.direction;
      }

      d.position = [
        d.sibling.position[0] + d.sibling.diag / 2 * d.direction,
        d.sibling.position[1] + d.diag / 2
      ];

      if((d.position[0] - d.diag/2 < -this.props.width/2) || (d.position[0] + d.diag/2 > this.props.width/2)) {
        d.direction = -d.direction;
        d.position[0]=d.sibling.position[0] + d.sibling.diag / 2 * d.direction;
      }



      if (availableSide <= d.side) {
        d.parent = d;
        d.direction = -d.sibling.direction;
        availableSide = d.side;
      }

      if (!ticks[d.article.hour] && hours[d.article.hour].articles > MIN_ARTICLES_FOR_TICKS) {
        ticks[d.article.hour] = {
          hour: moment(d.article.hour).format("HH:00"),
          date: moment(d.article.hour).format("DD MMM"),
          y: d.position[1],
        };
      }
      //console.log(d.position[1], d.article.id)
      this.squareMap.push([d.article.id,d.position[1]])

      height = Math.max(height, d.position[1] + d.diag / 2);

      return d;
    });

    infoBlocks.forEach(d => {
      let closestDate = d.key;
      let delta = moment(closestDate).unix();
      Object.keys(ticks).forEach(tick => {
        let diff = Math.abs(moment(d.key).unix() - moment(tick).unix());
        if(diff <= delta) {
          delta = diff;
          closestDate = tick;
        }
      })

      if(ticks[closestDate]) {
        visualTimelineTexts.push({
          text: `Il <b>${moment(d.key).format("DD MMM")}</b> abbiamo registrato <b>${d.value.total}</b> articoli.`,
          y: ticks[closestDate].y,
          data: d.value.slugs,
          closestDate,
          id: `vtext_${moment(closestDate).format('YYYY-MM-DD')}`,
          sentiments: d.value.sentiments,
        })
      }
    })

    this.setState({
      daysToElections,
      cells,
      articles,
      ticks,
      height,
      visualTimelineTexts,
    });
  }
  highlightSquare(id) {
    this.deHighlightSquare(this.selected);
    this.selected = id;
    this.bubbleSVG.querySelector(`g#square${id}`).classList.add('highlight');
  }
  deHighlightSquare(id) {
    //if(id == undefined) {
    if( typeof id === 'undefined' || id === null ) {
      return;
    }
    this.bubbleSVG.querySelector(`g#square${id}`).classList.remove('highlight');
  }
  selectArticle(id) {
    this.ArticleTexts.showText(id);
  }
  deselectArticle(id) {
    this.ArticleTexts.hideText(id);
  }
  _handleScroll(e) {
    const scrollTop = e.target.scrollTop - 100;
    this.currentScrollTop = scrollTop;
  }
  render() {

    const {cells, ticks, visualTimelineTexts} = this.state;

    const gradients = {};
    const rects = cells.map(d => {
      let bgColor = colors[d.slugs[0]];
      const gradientID = d.slugs.join("-");
      if (d.slugs.length > 1) {
        if (!gradients[gradientID]) {
          const step = 100 / (d.slugs.length - 1);
          gradients[gradientID] = (
            <linearGradient key={gradientID} id={gradientID}>
              {d.slugs.map((slug, i) => (
                <stop
                  key={`stop-${slug}`}
                  offset={`${i * step}%`}
                  stopColor={colors[slug]}
                />
              ))}
            </linearGradient>
          );
        }
        bgColor = `url(#${gradientID})`;
      }
      const sentiment = +d.article.sentiment;
      return (
        <g
          key={d.article.id}
          id={`square${d.article.id}`}
          className={`article ${gradientID}`}
          transform={`translate(${d.position[0]},${d.position[1]})`}
          data-pop={d.article.popularity}
          data-size={d.size}
        >
          <path
            className="square"
            d={`${radialArea(d.points)}Z`}
            style={{ fill: bgColor }}
          />
          {sentiment === 0 && (
            <NeutralFace position={d.position} width={d.diag} />
          )}
          {sentiment > 0 && (
            <PositiveFace position={d.position} width={d.diag} />
          )}
          {sentiment < 0 && (
            <NegativeFace position={d.position} width={d.diag} />
          )}
        </g>
      );
    });

    const timelineTicks = Object.values(ticks)
      .map(tick => {
        const tickStyle = {
          top: `${tick.y}px`
        };
        return (
          <li key={`${tick.date}-${tick.y}`} style={tickStyle}>
            <span>{tick.hour}<br/>{tick.date}</span>
          </li>
        );
      });

    const slugs = {};
    this.props.articles.forEach(article => {
      article.slugs.forEach(slug => {
        if (!slugs[slug]) {
          slugs[slug] = 0;
        }
        slugs[slug]++;
      });
    });

    const slugsSortedList = Object.entries(slugs).sort((a, b) => b[1] - a[1]);

    return (
      <div className="elezioni-parallax">
        <Standfirst showingHours={this.props.showingHours} articles={this.props.articles} slugsSortedList={slugsSortedList}/>
        <Keys showingHours={this.props.showingHours} articles={this.props.articles} slugsSortedList={slugsSortedList}/>

        <section className="squares-container parallax-layer parallax-layer-base">
          <ul className="timeline">{timelineTicks}</ul>
          <svg
            ref={el => (this.bubbleSVG = el)}
            width={this.props.width}
            height={this.state.height}
            className="squares-svg"
          >
            <defs>
              {Object.values(gradients)}
              <NeutralFaceDef />
              <PositiveFaceDef />
              <NegativeFaceDef />
            </defs>
            <g transform={`translate(${this.props.width / 2},0)`}>{rects}</g>
          </svg>
          <Methodology />
          <Who />
          <Footer />
        </section>
        <VisualTimeline texts={visualTimelineTexts} slugsSortedList={slugsSortedList} />
        <section className="squares-container parallax-layer parallax-layer-base">
          <ArticleTexts ref={el => (this.ArticleTexts = el)} articles={this.state.cells} width={this.props.width} selected={this.state.selected}/>
        </section>
      </div>
    );
  }
}

export default Bubbles;



// WEBPACK FOOTER //
// ./src/elezioni/components/Bubbles/Bubbles.js